import { INPUT_TYPES } from "constants/form-builder";
import { generateFileFingerprint, uploadMediaFiles } from "./uploadUtils";
import { formJsonDto } from "dto/form-json.dto";

export function toTitleCase(str) {
  if (!str) return "";
  return str
    .replace(/[-_]/g, " ") // Replace kebab-case and snake_case with spaces
    .replace(/\s+/g, " ") // Normalize multiple spaces
    .trim() // Remove leading and trailing spaces
    .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word
}

export const parseNumber = (value) => {
  let v = parseFloat(value);
  if (isNaN(v)) {
    v = 0;
  }
  return v;
};

export const sortResponses = (answers) => {
  const sortAnswers = structuredClone(answers);
  return sortAnswers.sort((a, b) => {
    //sort by answer.fieldIndex
    return a.fieldIndex - b.fieldIndex;
  });
};

export async function prepareFinalFormJson(formJson, toastId) {
  const filesToUploadMap = {};
  const fileLookUp = {};

  // Step 1: Collect files needing upload
  formJson.steps.forEach((step) => {
    step.fields.forEach((field) => {
      if (field.type === INPUT_TYPES.ICONRADIO) {
        field.options.forEach((option, index) => {
          const image = option.image;

          if (image instanceof File) {
            const key = `${field.id}_${index}`;
            const fingerprint = generateFileFingerprint(image);

            filesToUploadMap[key] = image;
            fileLookUp[fingerprint] = key;
          }
        });
      } else if (field.type === INPUT_TYPES.INFO_IMAGE) {
        const key = `${field.id}`;
        const image = field.config.image;
        if (image instanceof File) {
          const fingerprint = generateFileFingerprint(field.config.image);
          filesToUploadMap[key] = image;
          fileLookUp[fingerprint] = key;
        }
      }
    });
  });

  if (!Object.keys(filesToUploadMap).length) {
    return formJson;
  }
  // Step 2: Upload files
  const uploadedResult = await uploadMediaFiles({
    files: Object.values(filesToUploadMap),
    toastId,
    foldername: `questionnaire-icons`,
    fileLookUp,
    isPublic: true,
  });

  // Step 3: Update formJson
  const finalFormJson = JSON.parse(JSON.stringify(formJson)); // Deep clone

  finalFormJson.steps.forEach((step) => {
    step.fields.forEach((field) => {
      if (field.type === INPUT_TYPES.ICONRADIO) {
        field.options.forEach((option, index) => {
          const key = `${field.id}_${index}`;

          if (uploadedResult[key]) {
            // Replace image with just the S3 key
            option.image = uploadedResult[key].fileKey;
          } else if (typeof option.image === "string") {
          } else {
            option.image = null;
          }
        });
      } else if (field.type === INPUT_TYPES.INFO_IMAGE) {
        const key = `${field.id}`;
        if (uploadedResult[key]) {
          field.config.image = uploadedResult[key].fileKey;
        } else if (typeof field.config.image === "string") {
        } else {
          field.config.image = null;
        }
      }
    });
  });

  return finalFormJson;
}

export function validateFormJson(formJson) {
  try {
    // First validate with the existing schema
    formJsonDto.validateSync(formJson);

    // Additional custom validation for field options
    const fieldErrors = [];

    formJson.steps.forEach((step, stepIndex) => {
      step.fields.forEach((field, fieldIndex) => {
        // Check if field type requires options
        const optionRequiredTypes = ["SELECT", "RADIOGROUP", "MULTISELECT"];

        if (optionRequiredTypes.includes(field.type)) {
          if (
            !field.options ||
            !Array.isArray(field.options) ||
            field.options.length === 0
          ) {
            fieldErrors.push(
              `Step ${stepIndex + 1}, Field "${field.label || field.id}": ${
                field.type
              } field must have at least one option.`
            );
          } else {
            // Validate each option has required properties
            field.options.forEach((option, optionIndex) => {
              if (!option.label || !option.value) {
                fieldErrors.push(
                  `Step ${stepIndex + 1}, Field "${
                    field.label || field.id
                  }", Option ${
                    optionIndex + 1
                  }: Each option must have both label and value.`
                );
              }
            });
          }
        }
      });
    });

    // Return combined errors if any
    if (fieldErrors.length > 0) {
      return fieldErrors;
    }

    return null;
  } catch (error) {
    return error?.errors || [];
  }
}

export function combineFormJson(jsonPayloads = []) {
  const finalFormJson = {
    title: "Merged Form",
    userType: jsonPayloads[0]?.userType || "seller",
    steps: jsonPayloads
      .map((payload) =>
        payload.formJson.steps.map((step) => ({
          ...step,
          id: `${step.id}-${crypto.randomUUID()}`,
        }))
      )
      .flat(1),
  };

  return finalFormJson;
}

export function decodeHtml(html) {
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
}

export function formatBigNumber(value) {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  if (isNaN(num)) return value;

  // Use Intl.NumberFormat for compact notation (e.g., 1K, 2M)
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 1,
  }).format(num);
}
